import {
  ExternalDropZone,
  Upload,
  UploadFileInfo,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import axios from 'axios';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { ScanManagerErrorColumns } from 'src/assets/static/scan_manager/ScanManagerErrorTable';
import Modal from 'src/components/Modal';
import useBoolean from 'src/hooks/useBoolean';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import useInvoiceS3UploadApi from 'src/services/useInvoiceS3UploadApi';
import useReceiptS3UploadApi from 'src/services/useReceiptS3UploadApi';
import useS3InvoiceAttach, { S3UploadInvoiceAttachRequest } from 'src/services/useS3InvoiceAttach';
import useS3ReceiptAttach from 'src/services/useS3ReceiptAttach';
import { IReceipt as ReceiptDetailsResponse } from 'src/types/Receipt';
import { PreRequiredCheck, S3FileValidations, unProcessedFiles } from 'src/utils/validation';
import GridWrapper from '../GridWrapper';
import { S3UploadRes } from '../UploadFilesModal';
import { UploadPdfOnly } from '../guards/PermissionsList';

const uploadRef = React.createRef<Upload>();

interface UploadScansModalProps {
  onClose: VoidFunction;
  open: boolean;
  isInvoice: boolean;
  id: string;
  companyId: string;
  getDocument: () => Promise<InvoiceDetailsResponse | undefined> | Promise<ReceiptDetailsResponse>;
}

const UploadScansModal: FC<UploadScansModalProps> = ({
  onClose,
  open,
  isInvoice,
  id,
  companyId,
  getDocument,
}) => {
  const { S3Files: executeInvoiceUpload } = useInvoiceS3UploadApi();
  const { S3Files: executeReceiptUpload } = useReceiptS3UploadApi();
  const { S3InvoiceAttach } = useS3InvoiceAttach(parseInt(companyId), parseInt(id));
  const { S3ReceiptAttach } = useS3ReceiptAttach(parseInt(companyId), parseInt(id));
  const { confirm } = useConfirm();
  const { value: isUploading, setFalse: stopLoading } = useBoolean();
  const isPDFOnly = usePermission({ permissions: UploadPdfOnly });
  const [overallProgress, setOverallProgress] = useState(0);
  const onProgress = useCallback((event: any) => {
    const totalProgress = event.affectedFiles.reduce(
      (acc: number, file: UploadFileInfo) => acc + (file.progress || 0),
      0
    );
    const averageProgress = totalProgress / event.affectedFiles.length;
    setOverallProgress(averageProgress);
  }, []);
  const onSelectFilesClick = useCallback(() => {
    if (uploadRef) uploadRef?.current?.actionElement?.click();
  }, []);

  const hint = useMemo(
    () => (
      <span>
        <i>{isUploading ? 'Uploading....' : 'Drop files here to upload'}</i>
      </span>
    ),
    [isUploading]
  );
  const postAttachments = useCallback(
    async (
      attachMethod: Function,
      requestData: S3UploadRes[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
    }> => {
      const { processed, unProcessed } = preRequiredCheck;
      if (requestData.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck };
      }
      const modifiedReqData = requestData.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });
      const fileNames = modifiedReqData.map((file: any) => file.name);
      try {
        if (processed.length) {
          await attachMethod(modifiedReqData);
        }
        return {
          preRequiredCheck,
        };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        unProcessed.push(...errors);
        return {
          preRequiredCheck,
        };
      }
    },
    []
  );

  const putPresignedUrls = useCallback(
    async (
      s3response: S3UploadRes[],
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      awsResponse: string[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, awsResponse: [] };
      }
      const modifiedReqData = s3response.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });

      try {
        const awsResponse = await Promise.all(
          modifiedReqData.map(async (presignedUrls, index) => {
            // @ts-ignore
            const file = processedFiles[index].getRawFile();
            try {
              const response = await axios.put(presignedUrls.signedUrl, await file.arrayBuffer(), {
                headers: {
                  'Content-Type': file.type,
                },
              });
              const status = response.status === 200 ? 'success' : 'Fail'; // Or handle the response as needed
              return status;
            } catch (error) {
              return 'Fail';
            }
          })
        );
        if (awsResponse.includes('Fail')) {
          preRequiredCheck.processed = [];
          const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
            fileName: fileName.name,
            errorMessage: 'Failed to upload file',
          }));
          preRequiredCheck.processed = [];
          preRequiredCheck.unProcessed.push(...errors);
          return { preRequiredCheck, awsResponse: ['Fail'] };
        }
        return { preRequiredCheck, awsResponse };
      } catch (error) {
        preRequiredCheck.processed = [];
        const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
          fileName: fileName.name,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, awsResponse: ['Fail'] };
      }
    },
    []
  );

  const getSignedUrls = useCallback(
    async (
      uploadMethod: Function,
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      s3response: S3UploadInvoiceAttachRequest[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, s3response: [] };
      }
      const fileNames = processedFiles.map((file: any) => file.name);
      try {
        const s3response: S3UploadInvoiceAttachRequest[] = await uploadMethod(fileNames);
        return { preRequiredCheck, s3response };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, s3response: [] };
      }
    },
    []
  );
  const onStatusChange = useCallback(
    async (e: UploadOnStatusChangeEvent) => {
      const { processedFiles, preRequiredCheck } = await S3FileValidations(
        e?.affectedFiles,
        isPDFOnly
      );
      // s3 file > s3 response, getting s3 url
      const { preRequiredCheck: getSignedUrlsPreReqCheck, s3response } = isInvoice
        ? await getSignedUrls(executeInvoiceUpload, processedFiles, preRequiredCheck)
        : await getSignedUrls(executeReceiptUpload, processedFiles, preRequiredCheck);
      //  s3 response > success or fail , uploading to s3 with file and url
      const { preRequiredCheck: putPresignedUrlsPreReqCheck } = await putPresignedUrls(
        s3response,
        processedFiles,
        getSignedUrlsPreReqCheck
      );

      // s3 response >  preRequiredCheck
      const { preRequiredCheck: postSignedUrlsPreReqCheck } = isInvoice
        ? await postAttachments(S3InvoiceAttach, s3response, putPresignedUrlsPreReqCheck)
        : await postAttachments(S3ReceiptAttach, s3response, putPresignedUrlsPreReqCheck);
      const { processed, unProcessed } = postSignedUrlsPreReqCheck;
      onClose();

      confirm({
        title: 'File Upload',
        width: '650px',
        height: processed.length > 0 ? 'auto' : '380px',
        content: (
          <div className="font-bold ">
            <div className="font-bold ">
              {processed.length > 0 && (
                <>
                  {processed.length > 0 ? (
                    <>
                      <span className="font-extrabold text-lg m-2 text-green-600">
                        {processed.length}
                      </span>
                      file(s) uploaded successfully
                    </>
                  ) : (
                    ''
                  )}
                  {unProcessed?.length > 0 ? ' and' : ''}
                </>
              )}
              {unProcessed?.length > 0 && (
                <>
                  <span className="font-extrabold text-lg m-2 text-red-600">
                    {unProcessed?.length}
                  </span>
                  file(s) have failed
                  <GridWrapper
                    className="m-2 no_Veritcal_ScrollBar"
                    columns={ScanManagerErrorColumns}
                    dataSource={unProcessed}
                    DataItemKey={'fileName'}
                    pageable={false}
                    tableTextId="Errors"
                  />
                </>
              )}
            </div>
          </div>
        ),
        cancelButtonText: 'Close',
        cancelColor: 'info',
        hasNoConfirmButton: true,
      });
      setOverallProgress(0);
      getDocument();
      stopLoading();
    },
    [
      S3InvoiceAttach,
      S3ReceiptAttach,
      confirm,
      executeInvoiceUpload,
      executeReceiptUpload,
      getDocument,
      getSignedUrls,
      isInvoice,
      isPDFOnly,
      onClose,
      postAttachments,
      putPresignedUrls,
      stopLoading,
    ]
  );
  const CustomListItemUI = ({ files }: { files: UploadFileInfo[] }) => (
    <ul className="list-none w-full h-[20vh]">
      {files?.map((file: UploadFileInfo, index: number) => (
        <li key={file.name} className={`flex flex-col ${index !== 0 ? 'mt-2' : ''}`}>
          <div className="flex items-center justify-between">
            <span>{file.name}</span>
            <span className="text-sm text-gray-500">{file.size} KB</span>
          </div>
          <div className="w-full bg-gray-200 h-1 rounded-full mt-1">
            <div
              className="bg-info h-full rounded-full"
              style={{ width: `${file.progress || 0}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
  const isUploadDisabled = useMemo(() => overallProgress >= 1, [overallProgress]);
  return (
    <Modal title="Upload Files" closeModal={onClose} visible={open} width="500px" height="380px">
      <div onClick={onSelectFilesClick}>
        <ExternalDropZone uploadRef={uploadRef} customHint={hint} disabled={isUploadDisabled} />
      </div>
      <div className="">
        <Upload
          ref={uploadRef}
          batch={true}
          multiple={true}
          disabled={isUploadDisabled}
          defaultFiles={[]}
          onProgress={onProgress}
          withCredentials
          listItemUI={CustomListItemUI}
          onAdd={onStatusChange}
          accept={isPDFOnly ? '.pdf' : '.pdf, image/jpg, image/jpeg, .gif, .bmp, image/png'}
        />
        {overallProgress > 0 && <div>Overall Progress: {Math.round(overallProgress)}%</div>}
      </div>
    </Modal>
  );
};

export default UploadScansModal;
